import { memo, useCallback } from "react";
import { Link } from "react-router";

import { cn } from "@/lib/chillnn/cn";

import { Label } from "@/components/ui/label/label";
import { TableRow } from "@/components/ui/table/table";

import { PlanRateMatrixDailyTableCell } from "./cell";
import {
	RATE_MATRIX_HIGHLIGHT_ATTR,
	RATE_MATRIX_HIGHLIGHT_OVERLAY_CLASS,
} from "./highlight-class";
import { getRoomEditPath } from "../../../model/get-room-edit-path";
import { getCellForGuest, isUnpublishedSalePeriod } from "../../../model/types";

import type { PlanGroupedRateMatrixRow } from "../../../model/group-rows-by-plan-id";

type HoverTarget = {
	rowId: string;
	date: string;
};

type Props = {
	hotelID: string;
	row: PlanGroupedRateMatrixRow;
	dates: string[];
	adultNum: number;
	today: string;
	isRowHighlighted: boolean;
	highlightedDate: string | null;
	onHoverCell: (target: HoverTarget | null) => void;
};

const nameLinkClassName = "underline-offset-4 hover:underline";

/**
 * プラン×部屋の料金明細行。プラン見出しは別行（PlanRateMatrixPlanHeaderRow）に出す。
 */
export const PlanRateMatrixDailyTableRow = memo(
	({
		hotelID,
		row,
		dates,
		adultNum,
		today,
		isRowHighlighted,
		highlightedDate,
		onHoverCell,
	}: Props) => {
		const roomEditPath = getRoomEditPath({
			hotelID,
			roomID: row.roomID,
			isDummyRoom: row.isDummyRoom,
		});
		const showDummyRoomLabel = row.isDummyRoom;
		const showUnpublishedLabel = isUnpublishedSalePeriod(row.salePeriodLabel);

		const handleHoverDate = useCallback(
			(date: string) => {
				onHoverCell({ rowId: row.id, date });
			},
			[onHoverCell, row.id],
		);

		return (
			<TableRow className="relative isolate z-0 border-t border-t-zinc-200 bg-white transition-none hover:bg-white">
				{/* z は行の isolate 内だけ。全行 z-30 だと後続行の部屋名が前の行に乗る */}
				<td
					className={cn(
						"sticky left-0 z-10 w-64 min-w-64 border-r border-r-zinc-200 bg-white px-3 py-2 text-left align-top text-zinc-950 hover:bg-white",
						isRowHighlighted && RATE_MATRIX_HIGHLIGHT_OVERLAY_CLASS,
					)}
					{...(isRowHighlighted ? { [RATE_MATRIX_HIGHLIGHT_ATTR]: "row" } : {})}
				>
					<div className="flex flex-wrap items-center gap-1">
						{showDummyRoomLabel ? <Label variant="pink" text="ダミー部屋" size="xs" /> : null}
						{showUnpublishedLabel ? <Label variant="black" text="非公開" size="xs" /> : null}
						<Link to={roomEditPath} className={cn("text-xs text-zinc-600", nameLinkClassName)} viewTransition>
							{row.roomName}
						</Link>
					</div>
				</td>

				{dates.map((date) => (
					<PlanRateMatrixDailyTableCell
						key={`${row.id}-${date}`}
						cell={getCellForGuest(row, date, adultNum)}
						isToday={date === today}
						isRowHighlighted={isRowHighlighted}
						isColumnHighlighted={highlightedDate === date}
						onHover={() => handleHoverDate(date)}
					/>
				))}
			</TableRow>
		);
	},
);

PlanRateMatrixDailyTableRow.displayName = "PlanRateMatrixDailyTableRow";
